import MeReportFastApiView from "issara-sdk/apis/MeReportFastApiViewM";

import {
  ActionHandler,
  BaseSeqState,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";
import { downloadFile } from "react-lib/utils";

export const SEQUENCE_NAME = "MeReport" as const;
export const MAIN_CARD_NAME = "CardReportAgent" as const;

export const ACTIONS = {
  SET_LLM: "SET_LLM",
  RESET_SESSION: "RESET_SESSION",
  SEND_QUERY: "SEND_QUERY",
  EXPORT_FILE: "EXPORT_FILE",
  TOGGLE_CHART: "TOGGLE_CHART",
  SET_CHART_TYPE: "SET_CHART_TYPE",
} as const;

const DEFAULT_LLM: LlmModel = "qwen-plus";

const SYSTEM_WELCOME =
  "Welcome! I'm your Data Intelligence Agent. Ask me anything about the hospital systems or request data exports!";

const SYSTEM_RESET =
  "Session reset! Conversation history has been cleared. How can I help you now?";

export type LlmModel = "gpt-4o" | "gpt-5" | "qwen-plus" | "gemini-2.5-flash" | "gemini-2.5-pro";

export const LLM_MODEL_OPTIONS: { value: LlmModel; label: string }[] = [
  { value: "gpt-4o", label: "GPT-4o (OpenAI)" },
  { value: "gpt-5", label: "GPT-5 (OpenAI)" },
  { value: "qwen-plus", label: "Qwen Plus (Alibaba)" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
];

export type ExportFormat = "csv" | "excel";

export type ChartType = "bar" | "line" | "pie" | "doughnut";

export const CHART_TYPE_OPTIONS: { value: ChartType; label: string }[] = [
  { value: "bar", label: "Bar" },
  { value: "line", label: "Line" },
  { value: "pie", label: "Pie" },
  { value: "doughnut", label: "Doughnut" },
];

export type ChatMessageType = "system" | "user" | "agent" | "error";

export type ChatMessage = {
  id: string;
  type: ChatMessageType;
  text: string;
  rawSql?: string;
  exportLoading?: Partial<Record<ExportFormat, boolean>>;
  // for "error" type — original user query, used to retry
  retryQuery?: string;
  // chart visualization (lazy-loaded from /query/export with format=chartjs)
  chartData?: any;
  chartLoading?: boolean;
  chartType?: ChartType;
  chartVisible?: boolean;
};

type QueryResponse = {
  response: string;
  raw_sql?: string;
};

export type MeReportSequenceType = Partial<{
  sequenceIndex: "Action" | "START" | null;
  messages: ChatMessage[];
  llmModel: LlmModel;
  shouldResetSession: boolean;
  isLoading: boolean;
}> | null;

export type State = Partial<{
  MeReportSequence: MeReportSequenceType;
}>;

export type PickedState = PickMainState<"errorMessage" | "successMessage">;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { division?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SET_LLM]: { llmModel: LlmModel };
  [ACTIONS.RESET_SESSION]: {};
  [ACTIONS.SEND_QUERY]: { query: string };
  [ACTIONS.EXPORT_FILE]: { messageId: string; format: ExportFormat };
  [ACTIONS.TOGGLE_CHART]: { messageId: string };
  [ACTIONS.SET_CHART_TYPE]: { messageId: string; chartType: ChartType };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

export const StateInitial: State = {
  MeReportSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const extractErrorText = (err: any, fallback: string): string => {
  if (typeof err === "string") return err;
  return err?.responseData?.detail || err?.detail || err?.message || fallback;
};

export const Start: Handler<SeqState> = async (controller) => {
  controller.setState({
    MeReportSequence: {
      sequenceIndex: "Action",
      messages: [{ id: genId(), type: "system", text: SYSTEM_WELCOME }],
      llmModel: DEFAULT_LLM,
      shouldResetSession: false,
      isLoading: false,
    },
  });
};

export const Action: Handler<ActionType> = async (controller, params) => {
  const handlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SET_LLM]: HandleSetLlm,
    [ACTIONS.RESET_SESSION]: HandleResetSession,
    [ACTIONS.SEND_QUERY]: HandleSendQuery,
    [ACTIONS.EXPORT_FILE]: HandleExportFile,
    [ACTIONS.TOGGLE_CHART]: HandleToggleChart,
    [ACTIONS.SET_CHART_TYPE]: HandleSetChartType,
  };

  const handler = handlers[params.action] as Handler<Params<typeof params.action>> | undefined;

  handler?.(controller, params);
};

const HandleSetLlm: Handler<Params<"SET_LLM">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    MeReportSequence: { ...state.MeReportSequence, llmModel: params.llmModel },
  });
};

const HandleResetSession: Handler<Params<"RESET_SESSION">> = async (controller) => {
  const state = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...state.MeReportSequence,
      messages: [{ id: genId(), type: "system", text: SYSTEM_RESET }],
      shouldResetSession: true,
    },
  });
};

const HandleSendQuery: Handler<Params<"SEND_QUERY">> = async (controller, params) => {
  const seq = controller.getState().MeReportSequence;
  const queryText = params.query?.trim();

  if (!queryText || seq?.isLoading) return;

  const resetFlag = !!seq?.shouldResetSession;

  controller.setState({
    MeReportSequence: {
      ...seq,
      messages: [...(seq?.messages || []), { id: genId(), type: "user", text: queryText }],
      isLoading: true,
      shouldResetSession: false,
    },
  });

  const [data, err] = await MeReportFastApiView.post<QueryResponse>({
    endpoint: "/query",
    data: {
      query: queryText,
      llm_model: seq?.llmModel || DEFAULT_LLM,
      reset_session: resetFlag,
    },
  });

  const updatedSeq = controller.getState().MeReportSequence;

  const newMessage: ChatMessage = err
    ? {
        id: genId(),
        type: "error",
        text: `Error: ${extractErrorText(err, "Something went wrong.")}`,
        retryQuery: queryText,
      }
    : { id: genId(), type: "agent", text: data?.response || "", rawSql: data?.raw_sql };

  controller.setState({
    MeReportSequence: {
      ...updatedSeq,
      messages: [...(updatedSeq?.messages || []), newMessage],
      isLoading: false,
    },
  });
};

const HandleExportFile: Handler<Params<"EXPORT_FILE">> = async (controller, params) => {
  const seq = controller.getState().MeReportSequence;
  const target = (seq?.messages || []).find((m) => m.id === params.messageId);

  if (!target?.rawSql) return;

  let current = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...current.MeReportSequence,
      messages: (current.MeReportSequence?.messages || []).map((m) =>
        m.id === params.messageId
          ? { ...m, exportLoading: { ...(m.exportLoading || {}), [params.format]: true } }
          : m,
      ),
    },
  });

  const [, err, network] = await MeReportFastApiView.post<Blob>({
    endpoint: "/query/export",
    data: { query: target.rawSql, format: params.format },
    extra: { responseType: "blob" },
  });

  current = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...current.MeReportSequence,
      messages: (current.MeReportSequence?.messages || []).map((m) =>
        m.id === params.messageId
          ? { ...m, exportLoading: { ...(m.exportLoading || {}), [params.format]: false } }
          : m,
      ),
    },
  });

  if (network && !err) {
    const filename = params.format === "csv" ? "query_results.csv" : "query_results.xlsx";
    const mimeType =
      params.format === "csv"
        ? "text/csv;charset=utf-8"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    downloadFile(network, filename, mimeType);
  }

  if (err) {
    const current = controller.getState();

    controller.setState({
      errorMessage: {
        ...current.errorMessage,
        [MAIN_CARD_NAME]: extractErrorText(err, "Export failed."),
      },
    });
  }
};

const HandleSetChartType: Handler<Params<"SET_CHART_TYPE">> = async (controller, params) => {
  const current = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...current.MeReportSequence,
      messages: (current.MeReportSequence?.messages || []).map((m) =>
        m.id === params.messageId ? { ...m, chartType: params.chartType } : m,
      ),
    },
  });
};

const HandleToggleChart: Handler<Params<"TOGGLE_CHART">> = async (controller, params) => {
  const seq = controller.getState().MeReportSequence;
  const target = (seq?.messages || []).find((m) => m.id === params.messageId);

  if (!target?.rawSql) return;

  if (target.chartVisible) {
    const current = controller.getState();

    controller.setState({
      MeReportSequence: {
        ...current.MeReportSequence,
        messages: (current.MeReportSequence?.messages || []).map((m) =>
          m.id === params.messageId ? { ...m, chartVisible: false } : m,
        ),
      },
    });
    return;
  }

  let current = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...current.MeReportSequence,
      messages: (current.MeReportSequence?.messages || []).map((m) =>
        m.id === params.messageId
          ? { ...m, chartVisible: true, chartType: m.chartType || "bar" }
          : m,
      ),
    },
  });

  if (target.chartData) return;

  current = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...current.MeReportSequence,
      messages: (current.MeReportSequence?.messages || []).map((m) =>
        m.id === params.messageId ? { ...m, chartLoading: true } : m,
      ),
    },
  });

  const [data, err] = await MeReportFastApiView.post<any>({
    endpoint: "/query/export",
    data: { query: target.rawSql, format: "chartjs" },
  });

  // Detect non-numeric values BEFORE coercion so we can warn the user
  // (after coercion everything looks like 0 and we lose this signal).
  const hasNonNumeric = !!data?.datasets?.some((ds: any) =>
    (ds.data || []).some((v: any) => v !== null && v !== undefined && v !== "" && isNaN(Number(v))),
  );

  // Coerce dataset values to numbers (backend may return strings like "0", "5")
  let normalized = data && {
    ...data,
    _nonNumeric: hasNonNumeric,
    datasets: (data.datasets || []).map((ds: any) => ({
      ...ds,
      data: (ds.data || []).map((v: any) => Number(v) || 0),
    })),
  };

  // Pivot wide format → long format: when backend returns 1 label + N numeric datasets
  // (e.g. `SELECT COUNT(M) AS male, COUNT(F) AS female`), turn it into N labels + 1 dataset
  // so bar/pie/doughnut can render meaningful categories.
  if (normalized?.labels?.length === 1 && (normalized.datasets?.length || 0) > 1) {
    const PALETTE_BG = (normalized.datasets as any[]).map((ds) => ds.backgroundColor || "");
    const PALETTE_BORDER = (normalized.datasets as any[]).map((ds) => ds.borderColor || "");

    normalized = {
      ...normalized,
      labels: (normalized.datasets as any[]).map((ds) => ds.label ?? ""),
      datasets: [
        {
          label: normalized.labels[0] || "",
          data: (normalized.datasets as any[]).map((ds) => Number(ds.data?.[0]) || 0),
          backgroundColor: PALETTE_BG,
          borderColor: PALETTE_BORDER,
          borderWidth: 1,
        },
      ],
    };
  }

  if (err) {
    const current = controller.getState();

    controller.setState({
      MeReportSequence: {
        ...current.MeReportSequence,
        messages: (current.MeReportSequence?.messages || []).map((m) =>
          m.id === params.messageId ? { ...m, chartLoading: false, chartVisible: false } : m,
        ),
      },
      errorMessage: {
        ...current.errorMessage,
        [MAIN_CARD_NAME]: extractErrorText(err, "Failed to load chart."),
      },
    });
    return;
  }

  current = controller.getState();

  controller.setState({
    MeReportSequence: {
      ...current.MeReportSequence,
      messages: (current.MeReportSequence?.messages || []).map((m) =>
        m.id === params.messageId ? { ...m, chartData: normalized, chartLoading: false } : m,
      ),
    },
  });
};
